import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { I18n } from "aws-amplify";
import UserContext from './../../app/UserContext';

import { postUser } from './api/User'

import { postSuccessUserDetail as ToastPostSuccess, postErrorUserDetail as ToastPostError} from './../../shared/utils/toast';
import { ConstantEquals, USER } from './../../shared/constants/index';

import { Container, Row, Col, Breadcrumb, Form } from 'react-bootstrap'
import { Header, Footer, SidebarOffcanvas, NavItems } from './../../shared/components/Panels'
import { InputItem } from './form/InputItem'
import { SubmitButton } from './form/SubmitButton'

import { ValidMessage, ValidUsername, ValidMailaddress, ValidCompanyName } from './../user/Validate'

// 初期値を一元管理
const createInitialInput = (owner) => ({
  username: null,
  owner,
  address: '',
  postalCode: '',
  mailaddress: null,
  telephoneNumber: '',
  companyName: '',
  active: true,
  updatedAt: null,
  createdAt: null,
})

/**
 * - メールアドレスのValidationメッセージ：適切なメールアドレスではありません
 - 郵便番号のバリデーション：~~ハイフンなしを記述したほうが良い~~
- 郵便番号と電話番号のValidation：ハイフンを許容する
 */
const UserAdd = (props) => {
  const { user } = useContext(UserContext);
  const history = useHistory();

  const [showSidebar, setShowSidebar] = useState(false)

  const [formLoad, setFormLoad] = useState(false)
  const [input, setInput] = useState(() => createInitialInput(user?.username ?? ''))
  const [errors, setErrors] = useState({})

  // マウント時にinputを確実に初期化（userが取れた後の保険）
  useEffect(() => {
    setInput(createInitialInput(user?.username ?? ''))
    setErrors({})
  }, [user?.username])

  // 入力変更（不変更新 & 当該フィールドの一時エラー解除）
  const userInputChange = useCallback((key, value) => {
    setInput((prev) => ({ ...prev, [key]: value }))
    setErrors((prev) => {
      const { [key]: _omit, ...rest } = prev
      return rest
    })
  }, [])

  // 入力確認（エラー辞書のみ返す純粋関数）
  const validate = useCallback((data) => {
    /** @type {Record<string, string>} */
    const nextErrors = {}

    // ユーザ名
    if (Object.prototype.hasOwnProperty.call(data, USER.LABEL.USERNAME)) {
      const v = ValidUsername(data.username)
      if (!ConstantEquals(v, USER.VALIDATE.CLEAR)) {
        nextErrors[USER.LABEL.USERNAME] = ValidMessage(v)
      }
    }

    // 会社名
    if (Object.prototype.hasOwnProperty.call(data, USER.LABEL.COMPANY_NAME)) {
      const v = ValidCompanyName(data.companyName)
      if (!ConstantEquals(v, USER.VALIDATE.CLEAR)) {
        nextErrors[USER.LABEL.COMPANY_NAME] = ValidMessage(v)
      }
    }

    // メールアドレス
    if (Object.prototype.hasOwnProperty.call(data, USER.LABEL.MAILADDRESS)) {
      const v = ValidMailaddress(data.mailaddress)
      if (!ConstantEquals(v, USER.VALIDATE.CLEAR)) {
        nextErrors[USER.LABEL.MAILADDRESS] = ValidMessage(v)
      }
    }

    return nextErrors
  }, [])

  // Post： ユーザ
  const createUser = useCallback(async (payload) => {
    // API 失敗時は上位でトースト出すのでここは素通し
    const inserted = await postUser(payload)
    return inserted
  }, [])


  // onSubmit: 追加前処理
  const submitInsertUser = useCallback(
    async (event) => {

      // formデフォルトの挙動をキャンセル
      event.preventDefault();
      // イベントの伝播を止める
      event.stopPropagation();

      if (formLoad) return // 二重送信ガード

      const nextErrors = validate(input)
      if (Object.keys(nextErrors).length > 0) {
        setErrors(nextErrors)
        return
      }

      setFormLoad(true)
      try {
        await createUser(input)
        ToastPostSuccess()
        history.push({ pathname: '/users' })
      } catch (e) {
        // 失敗トースト（詳細はログだけに留めてUX維持）
        // eslint-disable-next-line no-console
        console.error('[UserAdd] postUser error:', e)
        ToastPostError()
      } finally {
        setFormLoad(false)
      }
    },
    [formLoad, input, validate, createUser, history]
  )

  // I18n をメモ化
  const i18n = useMemo(
    () => ({
      titleList: I18n.get('title_user_list'),
      titleAdd: I18n.get('title_user_add'),
      helpUsername: I18n.get('help_user_username'),
      labelUsername: I18n.get('label_user_username'),
      helpCompany: I18n.get('help_user_company_name'),
      labelCompany: I18n.get('label_user_company_name'),
      helpMail: I18n.get('help_user_mailaddress'),
      labelMail: I18n.get('label_user_mailaddress'),
    }),
    []
  )

  return (
    <>
      <Header onOpenSidebar={() => setShowSidebar(true)} />
      <SidebarOffcanvas show={showSidebar} onHide={() => setShowSidebar(false)} />

      <Container fluid className="p-0">
        <Row className="g-0">
          {/* 左サイドバー：大画面のみ固定表示（lg以上） */}
          <Col lg={2} className="p-0 d-none d-lg-block">
            <NavItems />
          </Col>

          {/* 右：元のコンテンツは基本的にそのまま（コメントも維持） */}
          <Col lg={10} className="p-3 main-content">
            <Container className="main-content">
              <Row>
                <Col xs={12} className="mt-1">
                  <Breadcrumb>
                    <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/users' }}>
                      {i18n.titleList}
                    </Breadcrumb.Item>
                    <Breadcrumb.Item active>{i18n.titleAdd}</Breadcrumb.Item>
                  </Breadcrumb>
                </Col>
              </Row>

              <Row>
                <Col xs={12} className="mt-3 mb-2">
                  <h3 className="text-break">{i18n.titleAdd}</h3>
                </Col>
              </Row>

              <hr />

              <Form noValidate onSubmit={submitInsertUser}>
                <InputItem
                  edit
                  load={formLoad}
                  formType="text"
                  help={i18n.helpUsername}
                  required
                  controlKey={USER.LABEL.USERNAME}
                  label={i18n.labelUsername}
                  error={errors[USER.LABEL.USERNAME]}
                  value={input[USER.LABEL.USERNAME] ?? ''}
                  onChange={(evt) => userInputChange(USER.LABEL.USERNAME, evt.target.value)}
                />

                <InputItem
                  edit
                  load={formLoad}
                  formType="text"
                  help={i18n.helpCompany}
                  controlKey={USER.LABEL.COMPANY_NAME}
                  label={i18n.labelCompany}
                  error={errors[USER.LABEL.COMPANY_NAME]}
                  value={input[USER.LABEL.COMPANY_NAME] ?? ''}
                  onChange={(evt) => userInputChange(USER.LABEL.COMPANY_NAME, evt.target.value)}
                />

                <InputItem
                  edit
                  load={formLoad}
                  formType="text"
                  help={i18n.helpMail}
                  controlKey={USER.LABEL.MAILADDRESS}
                  label={i18n.labelMail}
                  error={errors[USER.LABEL.MAILADDRESS]}
                  value={input[USER.LABEL.MAILADDRESS] ?? ''}
                  onChange={(evt) => userInputChange(USER.LABEL.MAILADDRESS, evt.target.value)}
                />

                <hr />
                <div className="text-end">
                  <SubmitButton isLoading={formLoad} disabled={formLoad} />
                </div>
              </Form>
            </Container>
          </Col>
        </Row>
      </Container>

      <Footer />
    </>
  )
}

export default UserAdd